import { useMemo, useState } from 'react';
import { useAppSelector } from '../../redux/store';
import useGetData from '../../Services/useGetData';
import { BankAccounts } from './BankAccounts';
import { ChartDashboard, ChartPoint } from './Chart';
import { IncomeAndExpense } from './IncomeAndExpense';
import { MostSoldProducts } from './MostSoldProducts';
import { SalesByStore } from './SalesByStore';
import * as S from './styles';
import globals from '../../Globals.json';
import { CategorySoldProducts } from './CategorySoldProducts';
import { SalesTemporary } from './SalesTemporary';
import { DateFilterBar } from './DateFilterBar';
import { KpiCards } from './KpiCards';
import { PaymentMethods } from './PaymentMethods';
import { SalesByHour } from './SalesByHour';
import { SalesBySeller } from './SalesBySeller';
import { useSalesAnalytics } from './useSalesAnalytics';
import { useSalesTemporaryTotal } from './useSalesTemporaryTotal';
import {
  DashboardPreset,
  buildDashboardQuery,
  describeRange,
  eachDayOfRange,
  eachMonthOfRange,
  formatDateParam,
  granularityFromPreset,
  isRangeReady,
  rangeFromPreset,
} from './dateFilters';

interface SalesMonth {
  month: number;
  month_name: string;
  total_sales: string;
  total_value: string;
  year: number;
  day?: number | string;
  day_name?: string;
  date?: string;
}

const MONTH_SHORT = [
  'Jan',
  'Fev',
  'Mar',
  'Abr',
  'Mai',
  'Jun',
  'Jul',
  'Ago',
  'Set',
  'Out',
  'Nov',
  'Dez',
];

const MONTH_FULL = [
  'Janeiro',
  'Fevereiro',
  'Março',
  'Abril',
  'Maio',
  'Junho',
  'Julho',
  'Agosto',
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro',
];

const WEEKDAY_SHORT = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

const toNumber = (value: unknown): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

/**
 * Descobre a chave (`YYYY-MM-DD` ou `YYYY-MM`) de um registro do
 * `sales-by-month`. O endpoint varia conforme o agrupamento: no modo diário
 * manda `date`/`day`, no mensal manda `year` + `month`.
 */
function pointKey(item: SalesMonth, granularity: 'day' | 'month'): string | null {
  if (granularity === 'month') {
    if (!item.year || !item.month) return null;
    return `${item.year}-${String(item.month).padStart(2, '0')}`;
  }

  if (item.date) return String(item.date).slice(0, 10);
  if (item.year && item.month && item.day) {
    return `${item.year}-${String(item.month).padStart(2, '0')}-${String(item.day).padStart(2, '0')}`;
  }
  return null;
}

export function DashoardComponent() {
  const { storeActive } = useAppSelector(({ storeMeReducer }) => storeMeReducer.value);

  // O mês corrente inteiro é o período inicial da tela.
  const [preset, setPreset] = useState<DashboardPreset>('mes');
  const [{ startDate, endDate }, setRange] = useState(() => rangeFromPreset('mes'));

  // Só consultamos a API quando o intervalo está completo e coerente; assim um
  // estado intermediário do input de data nunca vira um 422 na tela.
  const rangeReady = isRangeReady(startDate, endDate);
  const granularity = granularityFromPreset(preset, { startDate, endDate });

  const handleSelectPreset = (next: Exclude<DashboardPreset, 'custom'>) => {
    setPreset(next);
    setRange(rangeFromPreset(next));
  };

  const handleChangeStartDate = (value: string) => {
    setPreset('custom');
    setRange((current) => ({ ...current, startDate: value }));
  };

  const handleChangeEndDate = (value: string) => {
    setPreset('custom');
    setRange((current) => ({ ...current, endDate: value }));
  };

  /**
   * `sales-by-month` agrupa por dia quando recebe `last_30_days=1` e por mês
   * quando recebe um intervalo. "7 dias" e "Este mês" usam o modo diário e o
   * recorte da janela é feito aqui no cliente, já que o endpoint sempre
   * devolve os últimos 30 dias.
   *
   * O modo diário só serve se o início do intervalo estiver dentro desses 30
   * dias; fora disso pedimos o agrupamento por mês, senão o gráfico ficaria
   * vazio.
   *
   * "Este mês" é exceção: num mês de 31 dias, no último dia o dia 1º fica um
   * dia fora da janela do backend. Cair para o agrupamento mensal aí trocaria
   * as 31 barras diárias por uma única barra do mês — a tela pareceria
   * quebrada num dia em que nada mudou. Preferimos manter o modo diário e
   * deixar a barra do dia 1º zerada, que é um desvio bem menor. Deixa de
   * acontecer quando o `sales-by-month` aceitar intervalo no modo diário.
   *
   * Atenção ao modo mensal: cada barra é o mês inteiro, mesmo quando o filtro
   * cobre só parte dele. O total do cabeçalho vem do `sales-summary` e respeita
   * o intervalo, então as barras das pontas podem somar mais que ele — por isso
   * marcamos essas barras como parciais no tooltip.
   */
  const withinLast30Days = useMemo(() => {
    const limit = new Date();
    limit.setDate(limit.getDate() - 29);
    return startDate >= formatDateParam(limit);
  }, [startDate]);

  const useDailyMode =
    granularity === 'day' && (preset === 'mes' || withinLast30Days);

  /**
   * Granularidade que os dados realmente têm. Um intervalo personalizado
   * antigo pede dia mas só existe agrupado por mês na API — o gráfico precisa
   * montar as chaves no mesmo formato da resposta.
   */
  const effectiveGranularity = useDailyMode ? 'day' : 'month';

  const { data, isLoading, isFetching } = useGetData({
    path: globals?.api?.salesMonthByStore,
    query: buildDashboardQuery({
      storeId: storeActive?.id,
      startDate,
      endDate,
      last30Days: useDailyMode,
    }),
    enabled: !!storeActive?.id && (useDailyMode || rangeReady),
  });

  const analytics = useSalesAnalytics({
    storeId: storeActive?.id,
    startDate,
    endDate,
  });

  const temporary = useSalesTemporaryTotal(storeActive?.id);

  /**
   * Série completa do gráfico: montamos todos os dias (ou todos os meses) do
   * período e preenchemos com o retorno da API. Assim "7 dias" mostra 7 barras
   * e "Este ano" mostra as 12 do ano, mesmo nos períodos sem faturamento.
   */
  const chartPoints = useMemo<ChartPoint[]>(() => {
    if (!rangeReady) return [];

    const rows: SalesMonth[] = data?.data ?? [];
    const totals = new Map<string, number>();

    rows.forEach((row) => {
      const key = pointKey(row, effectiveGranularity);
      if (!key) return;
      totals.set(key, (totals.get(key) ?? 0) + toNumber(row.total_value));
    });

    if (effectiveGranularity === 'month') {
      // Meses das pontas só entram por inteiro se o filtro começar no dia 1º e
      // terminar no último dia; caso contrário a barra cobre mais que o filtro.
      const startsMonth = startDate.slice(8, 10) === '01';
      const endLast = new Date(
        Number(endDate.slice(0, 4)),
        Number(endDate.slice(5, 7)),
        0
      ).getDate();
      const endsMonth = Number(endDate.slice(8, 10)) === endLast;

      const firstKey = startDate.slice(0, 7);
      const lastKey = endDate.slice(0, 7);

      return eachMonthOfRange(startDate, endDate).map((key) => {
        const monthIndex = Number(key.slice(5, 7)) - 1;
        const name = `${MONTH_FULL[monthIndex] ?? key} de ${key.slice(0, 4)}`;
        const partial =
          (key === firstKey && !startsMonth) || (key === lastKey && !endsMonth);

        return {
          key,
          label: MONTH_SHORT[monthIndex] ?? key,
          fullLabel: partial ? `${name} (mês completo)` : name,
          total: totals.get(key) ?? 0,
        };
      });
    }

    return eachDayOfRange(startDate, endDate).map((key) => {
      const date = new Date(`${key}T00:00:00`);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');

      return {
        key,
        label: `${day}/${month}`,
        fullLabel: `${WEEKDAY_SHORT[date.getDay()]}, ${day}/${month}/${date.getFullYear()}`,
        total: totals.get(key) ?? 0,
      };
    });
  }, [data, effectiveGranularity, startDate, endDate, rangeReady]);

  // A tela nunca fica presa num loading global: a estrutura da dashboard é
  // montada de imediato e cada painel cuida do próprio estado de carregamento,
  // aparecendo conforme a sua endpoint responde.
  return (
    <>
      <h3 className="text-xl mx-3">Dashboard</h3>

      {/* Filtros de data */}
      <DateFilterBar
        startDate={startDate}
        endDate={endDate}
        preset={preset}
        isFetching={isFetching || analytics.isFetching}
        onChangeStartDate={handleChangeStartDate}
        onChangeEndDate={handleChangeEndDate}
        onSelectPreset={handleSelectPreset}
      />

      {/* Dashboard */}
      <S.Dashboard>
        <ChartDashboard
          points={chartPoints}
          granularity={effectiveGranularity}
          periodTotal={analytics.totalValue}
          rangeLabel={rangeReady ? describeRange({ startDate, endDate }) : undefined}
          isFetching={isFetching || analytics.isFetching}
          isLoading={isLoading || analytics.isLoading}
        />

        <KpiCards
          isLoading={analytics.isLoading || temporary.isLoading}
          totalValue={analytics.totalValue}
          averageTicket={analytics.averageTicket}
          totalOrders={analytics.totalOrders}
          serviceTaxTotal={analytics.serviceTaxTotal}
          serviceTaxTables={analytics.serviceTaxTables}
          serviceTaxDelivery={analytics.serviceTaxDelivery}
          temporaryTotal={temporary.total}
          temporaryCount={temporary.count}
          temporaryTables={temporary.tables}
          temporaryDelivery={temporary.delivery}
          customersCount={analytics.customersCount}
          newCustomers={analytics.newCustomers}
        />

        <section className="income-expense">
          <MostSoldProducts title="Produtos Mais Vendidos" start={0} end={7} startDate={startDate} endDate={endDate} />
          <MostSoldProducts title="Produtos Menos Vendidos" start={0} end={7} startDate={startDate} endDate={endDate} />
        </section>

        <section className="income-expense">
          <SalesByHour
            hours={analytics.hours}
            isFetching={analytics.isFetching}
            errorMessage={analytics.errorMessage}
          />
          <SalesBySeller
            sellers={analytics.sellers}
            isFetching={analytics.isFetching}
            errorMessage={analytics.errorMessage}
          />
        </section>

        <section className="income-expense">
          <PaymentMethods
            payments={analytics.payments}
            isFetching={analytics.isFetching}
            errorMessage={analytics.errorMessage}
          />
          <CategorySoldProducts title="Vendas por categoria" start={0} end={6} startDate={startDate} endDate={endDate} />
        </section>

        <section className="income-expense">
          <IncomeAndExpense type="receitas" startDate={startDate} endDate={endDate} />
          <IncomeAndExpense type="despesas" startDate={startDate} endDate={endDate} />
        </section>

        <section className="income-expense">
          <BankAccounts />
          <SalesByStore startDate={startDate} endDate={endDate} />
        </section>

        <section className="income-expense">
          <SalesTemporary />
        </section>
      </S.Dashboard>
    </>
  );
}
